import { AfterViewInit } from '@angular/core';
import { Component, Input, EventEmitter, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateTime } from 'luxon';
import { NgClass, NgStyle } from '@angular/common';
import { MaskedTextBoxModule } from '@syncfusion/ej2-angular-inputs';

@Component({
    selector: 'app-duration-input',
    templateUrl: './duration-input.component.html',
    styleUrls: ['./duration-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DurationInputComponent),
            multi: true,
        }
    ],
    standalone: true,
    imports: [
        MaskedTextBoxModule,
        NgClass,
        NgStyle,
    ],
})
export class DurationInputComponent implements ControlValueAccessor, AfterViewInit {
  @Output()
  public durationChange: EventEmitter<number> = new EventEmitter<number>();

  @Input()
  public disabled = false;

  @Input()
  public styleClass: string;

  @Input()
  public inputStyle: {[klass: string]: any;};

  @Input()
  public set duration(duration: number) {
    if (duration) {
      this._duration = duration;
    } else {
      this._duration = null;
    }
    this.resetInnerDuration();
  }

  @Input()
  public set minDuration(minDuration: number) {
    if (minDuration) {
      this._minTotalMinutes = minDuration;
    } else {
      this._minTotalMinutes = 0;
    }
  }

  @Input()
  public set maxDuration(maxDuration: number) {
    if (maxDuration) {
      this._maxTotalMinutes = maxDuration;
    } else {
      this._maxTotalMinutes = 24 * 60;
    }
  }

  public set innerDuration(durationStr: string) {
    if (durationStr === this._innerDuration || !this.viewInitialized) {
      return;
    }
    this.onTouched();

    if (durationStr && durationStr.length === 4) {
      const hours = Number.parseInt(durationStr.substring(0, 2), 10);
      const minutes = Number.parseInt(durationStr.substring(2, 4), 10);
      if (hours > 24 || minutes > 59) {
        this.resetInnerDuration();
        return;
      }

      this._duration = 60 * hours + minutes;
      let timeNeedsReset = false;
      if (this._duration > this._maxTotalMinutes) {
        this._duration = this._maxTotalMinutes;
        timeNeedsReset = true;
      } else if (this._duration < this._minTotalMinutes) {
        this._duration = this._minTotalMinutes;
        timeNeedsReset = true;
      }

      this.durationChange.emit(this._duration);
      this.onChange(this._duration);
      if (timeNeedsReset) {
        this.resetInnerDuration();
        return;
      }
    } else if (!durationStr || durationStr.length === 0) {
      this._duration = null;
      this.durationChange.emit(this._duration);
      this.onChange(this._duration);
    }
    this._innerDuration = durationStr;
  }

  public get innerDuration(): string {
    return this._innerDuration;
  }

  private _duration: number;
  private _innerDuration: string;
  private _minTotalMinutes = 0;
  private _maxTotalMinutes = 24 * 60;
  private viewInitialized = false;

  constructor() { }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
  }

  public onChange: any = () => {};
  public onTouched: any = () => {};

  public writeValue(duration: number): void {
    this.duration = duration;
  }

  public registerOnChange(fn): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onBlur() {
    this.resetInnerDuration();
  }

  private resetInnerDuration() {
    if (this._duration != null) {
      if (this._duration >= 24 * 60) {
        this._innerDuration = '24:00';
      } else {
        this._innerDuration = DateTime.now().startOf('day').plus({minutes: this._duration}).toFormat('HH:mm');
      }
    } else {
      this._innerDuration = null;
    }
  }
}
